import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Switch, Modal, Input, Button, Dropdown, message, Tooltip as AntTooltip } from 'antd';
import {
  AlertOutlined,
  CalendarOutlined,
  CarOutlined,
  CheckCircleOutlined,
  DownOutlined,
  FilePdfOutlined,
  FileSearchOutlined,
  FormOutlined,
  LoadingOutlined,
  MessageOutlined,
  PrinterOutlined,
  IdcardOutlined,
  QrcodeOutlined,
  ShoppingOutlined,
  RedoOutlined,
  SafetyCertificateOutlined,
  SwapOutlined,
  ThunderboltOutlined,
  UndoOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { post, get } from '../../../../services/ApiService';
import {
  DateFormater,
  DateTimeFormater,
  truncateText,
  getStatus,
  determineCarrier,
  getCarrierLabel,
  getShippedDate,
  getServiceEstimatedDelivery,
  getTrackingEstimatedDelivery,
  canScheduleDispatch,
  isDispatchScheduledForLater,
  formatDaysUntilDispatch
} from "../../../../services/Helper";
import TrackingModal from '../../../../components/TrackingModal';
import ReplacementModal from './ReplacementModal';
import AlternativeStockModal from './AlternativeStockModal';
import ReturnQrModal from './ReturnQrModal';
import AddTrackingModal from './AddTrackingModal';
import DispatchDateModal from './DispatchDateModal';
import IdVerificationModal from './IdVerificationModal';
import IdCheckStatusPanel from './IdCheckStatusPanel';
import EscalationModal from '../../Escalations/EscalationModal';
import usePermissionCheck from '../../../../hooks/usePermissionCheck';
import { usePermissions } from '../../../../redux/PermissionsContext';
import { ESCALATION_TYPES, ESCALATION_TYPE_KEYS, countReplies } from '../../Escalations/escalationTypes';
import useEscalationActions from '../../Escalations/useEscalationActions';
const { TextArea } = Input;

const TRANSFERABLE_STATUSES = ["SEND_TO_RM", "PRIME_READY", "PROCESSED"];
const LOCATION_TRANSFER_TARGETS = {
  "MI Health Longcroft House": {
    location: "Berkeley Care Unit",
    fulfilmentBy: "PHARMANEST",
  },
  "Berkeley Care Unit": {
    location: "MI Health Longcroft House",
    fulfilmentBy: "PILLPHARM",
  },
};

const isRoyalMailCarrier = (carrier) => !carrier || carrier === "RM";

const isAmazonCarrier = (order) => {
  const carrier = String(order?.carrier || "").toUpperCase();
  return carrier === "AMZ" || carrier === "AMAZON";
};

const isAmazonPurchasedLabel = (order) =>
  Boolean(order?.isPrime || order?.isShipPlus || isAmazonCarrier(order));

const isEbayAmazonCarrier = (order) =>
  String(order?.platform || "").toLowerCase() === "ebay" && isAmazonCarrier(order);

const getTransferCarrierCopy = (order) => {
  if (isAmazonPurchasedLabel(order)) {
    return {
      carrierName: "Amazon Shipping",
      carrierShort: "Amazon",
      pendingLabel: isEbayAmazonCarrier(order) ? "Ready for RM" : "Prime Ready",
    };
  }

  return {
    carrierName: "Royal Mail",
    carrierShort: "RM",
    pendingLabel: "Ready for RM",
  };
};

const orderHasInvoiceRecords = (order) =>
  (order?.invoices || []).some((group) =>
    (group.invoiceRecord || []).some(
      (record) => record?.invoiceId && Number(record.quantity) > 0
    )
  );

const orderHasAnyInvoices = (order) =>
  (order?.invoices || []).some((group) => (group.invoiceRecord || []).length > 0);

const getTransferTarget = (order) => {
  const currentLocation = order?.location || "MI Health Longcroft House";
  return LOCATION_TRANSFER_TARGETS[currentLocation] || null;
};

const canTransferOrderLocation = (order, canTransfer) => {
  if (!canTransfer || !order) return false;
  if (order.fulfilmentBy === "FBA") return false;
  if (!TRANSFERABLE_STATUSES.includes(order.status)) return false;
  if (
    order.status === "PROCESSED" &&
    !isAmazonPurchasedLabel(order) &&
    !isRoyalMailCarrier(order.carrier)
  ) return false;
  return Boolean(getTransferTarget(order));
};

const SPECIAL_DELIVERY_STATUSES = ["SEND_TO_RM", "PENDING", "ASSESSMENT_REQUIRED", "NOT_STARTED"];

const canSetSpecialDelivery = (order, canEdit) => {
  if (!canEdit || !order) return false;
  if (order.fulfilmentBy === "FBA") return false;
  if (order.isPrime || order.isShipPlus) return false;
  if (!SPECIAL_DELIVERY_STATUSES.includes(order.status)) return false;
  if (order.postageCode === "SEB") return false;
  const carrier = String(order.carrier || "").toUpperCase();
  return !carrier || carrier === "RM";
};

const RESET_STATUSES = ["PROCESSED", "SHIPPED"];
const TRACKABLE_POSTAGE_PREFIXES = ["TPN", "TPS", "SD1", "SD2", "TRN", "TRS", "MTI", "SEB"];

const isTrackedPostageCode = (postageCode) => {
  const code = String(postageCode || "").trim().toUpperCase();
  return TRACKABLE_POSTAGE_PREFIXES.some((prefix) => code.startsWith(prefix));
};

const canResetProcessedOrder = (order, canReset) => {
  if (!canReset || !order) return false;
  if (order.fulfilmentBy === "FBA") return false;
  if (!RESET_STATUSES.includes(order.status)) return false;
  if (
    order.status === "SHIPPED" &&
    determineCarrier(order.trackingNumber, order) !== "AMAZON" &&
    !isTrackedPostageCode(order.postageCode)
  ) {
    return false;
  }
  return isAmazonPurchasedLabel(order) || isRoyalMailCarrier(order.carrier);
};

const canRegenerateShippingLabel = (order, canSendToCourier) => {
  if (!canSendToCourier || !order) return false;
  if (order.fulfilmentBy === "FBA") return false;
  if (order.status !== "PROCESSED") return false;
  return isAmazonPurchasedLabel(order) || isRoyalMailCarrier(order.carrier);
};

const OrderDetailCard = ({
  order,
  openCases,
  openEscalations = [],
  setOpenEscalations,
  customer,
  assessment,
  idCheckSummary,
  setIdCheckSummary,
  setCustomer,
  canMarkVerified,
  mailLogs,
  canEdit,
  canStatusEdit,
  canAutomation,
  reminderLoading,
  reminder,
  handleReminderChange,
  orderStatusChange,
  onLocationTransferred,
  setOrder
}) => {

  const [isLoading, setIsLoading] = useState(false);
  const [pgdLoading, setPgdLoading] = useState(false);
  const [showInvoiceAddressModal, setShowInvoiceAddressModal] = useState(false);
  const [invoiceType, setInvoiceType] = useState(null);
  const [invoiceAddress, setInvoiceAddress] = useState(null);
  // Tracking Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [trackingEstimatedDelivery, setTrackingEstimatedDelivery] = useState(null);
  // Replacement Modal State
  const [isReplacementModalOpen, setIsReplacementModalOpen] = useState(false);
  const [isAlternativeStockModalOpen, setIsAlternativeStockModalOpen] = useState(false);
  const [existingReplacements, setExistingReplacements] = useState([]);
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  // QR Code Return State
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [existingReturn, setExistingReturn] = useState(null);
  const [returnLoading, setReturnLoading] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferLoading, setTransferLoading] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [showSpecialDeliveryModal, setShowSpecialDeliveryModal] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [specialDeliveryLoading, setSpecialDeliveryLoading] = useState(false);
  const [escalationModalType, setEscalationModalType] = useState(null);
  const [escalationLoading, setEscalationLoading] = useState(false);
  const [isAddTrackingModalOpen, setIsAddTrackingModalOpen] = useState(false);
  const [isDispatchDateModalOpen, setIsDispatchDateModalOpen] = useState(false);

  const canReplacement = usePermissionCheck(['order.replacement_create']);
  const canAlternativeStock = usePermissionCheck(['order.replacement_create']);
  const canReturn = usePermissionCheck(['order.return_create']);
  const canTransfer = usePermissionCheck(['order.transfer_location']);
  const canReset = usePermissionCheck(['order.reset']);
  const canTrack = usePermissionCheck(['order.tracker']);
  const { hasPermission } = usePermissions();
  const canSendToCourier = usePermissionCheck(['order.send_to_rm']);
  const canSendMsg = usePermissionCheck(['order.send_message']);
  const [labelLoading, setLabelLoading] = useState(false);
  const [awaitingReplyLoading, setAwaitingReplyLoading] = useState(false);

  useEffect(() => {
    setTrackingEstimatedDelivery(null);
    if (!order?.trackingNumber || !canTrack) return;

    let cancelled = false;
    fetchTrackingDetails(order.trackingNumber, order)
      .then((res) => {
        if (!cancelled && res?.success) {
          setTrackingEstimatedDelivery(getTrackingEstimatedDelivery(res.data));
        }
      })
      .catch(() => {
        // non-critical
      });
    return () => { cancelled = true; };
  }, [order, canTrack]);

  useEffect(() => {
    if (order?._id) {
      fetchReplacements(order._id);
      // Guarded: without the permission this call 403s and the axios
      // interceptor would pop a "permission denied" toast on page load.
      if (canReturn) fetchReturns(order._id);
    }
  }, [order?._id, canReturn]);

  const fetchReplacements = async (orderId) => {
    try {
      const res = await get(`api/replacements/order/${orderId}`);
      if (res.success) {
        setExistingReplacements(res.replacements);
      }
    } catch (_) {
      // silently fail — non-critical
    }
  };

  const handleTrackingAdded = (trackingUpdate) => {
    setOrder((prev) => ({ ...prev, ...trackingUpdate }));
    setIsAddTrackingModalOpen(false);
  };

  const handleDispatchDateSaved = (dispatchDateUpdate) => {
    setOrder((prev) => ({ ...prev, ...dispatchDateUpdate }));
    setIsDispatchDateModalOpen(false);
  };

  const handleReplacementSuccess = (response) => {
    setExistingReplacements((prev) => [
      { _id: response.replacementId, royalMailOrderRef: response.royalMailOrderRef, status: 'completed', createdAt: response.createdAt },
      ...prev,
    ]);
  };

  const fetchReturns = async (orderId) => {
    try {
      const res = await get(`api/returns/order/${orderId}`);
      if (res.success) {
        setExistingReturn(res.returns.find((r) => r.status === 'created') || null);
      }
    } catch (_) {
      // silently fail — non-critical
    }
  };

  const handleReturnClick = () => setIsReturnModalOpen(true);

  const showAwaitingReplyAction = canSendMsg && Boolean(setOrder) && order?.status === "PENDING";

  const handleAwaitingReply = async () => {
    if (!order?._id || !setOrder || awaitingReplyLoading) return;
    const next = !order.awaitingReply;
    setAwaitingReplyLoading(true);
    try {
      const res = await post(`api/orders/${order._id}/awaiting-reply`, {
        awaitingReply: next,
      });
      if (res?.success) {
        setOrder((prev) => ({
          ...prev,
          awaitingReply: res.awaitingReply,
          awaitingReplyAt: res.awaitingReplyAt,
        }));
        message.success(next ? "Marked as awaiting reply" : "Awaiting reply cleared");
      } else {
        message.error(res?.error || "Failed to update awaiting reply");
      }
    } catch (error) {
      if (!error.handledByInterceptor) {
        message.error(error?.response?.data?.error || "Failed to update awaiting reply");
      }
    } finally {
      setAwaitingReplyLoading(false);
    }
  };

  const handleReturnCreate = async ({ reason, notes }) => {
    setReturnLoading(true);
    try {
      const response = await post('api/returns/create', { orderId: order._id, reason, notes });

      if (response.success) {
        setExistingReturn(response);
        message.success(
          response.alreadyExists
            ? 'This order already has a return. Showing the existing QR code and label.'
            : 'QR code return created. QR code and return label are ready.'
        );
        if (Array.isArray(response.warnings)) {
          response.warnings.forEach((w) => message.warning(w.message, 8));
        }
      }
    } catch (error) {
      if (!error?.handledByInterceptor) {
        message.error(error?.response?.data?.message || 'Failed to create the return');
      }
    } finally {
      setReturnLoading(false);
    }
  };

  const handleTrackOrder = async (trackingNumber) => {
    if (!trackingNumber) return;

    setIsModalOpen(true);
    setModalLoading(true);
    setModalData({ orderInfo: order });

    try {
      const res = await fetchTrackingDetails(trackingNumber, order);

      if (res.success) {
        setModalData({
          ...res.data,
          orderInfo: order
        });
      } else {
        setModalData({
          orderInfo: order,
          error: res.error || "Tracking details not found."
        });
      }
    } catch (error) {
      // console.error(error);
      setModalData({
        orderInfo: order,
        error: "Failed to track item. Service may be unavailable."
      });
    } finally {
      setModalLoading(false);
    }
  };

  const getOrderInvoice = async (addressType, selectedInvoiceType) => {
    try {
      setIsLoading(true);
      closeInvoiceModal();
      const response = await get(`api/orders/invoice/${order?._id}?addressType=${addressType}&invoiceType=${selectedInvoiceType}`);
      setIsLoading(false);
      if (response.success) {
        window.open(response.data, '_blank');
      } else {
        message.error(response.error);
      }
    } catch (error) {
      setIsLoading(false);
      message.error('Failed to fetch order invoice');
      console.error('Error fetching order invoice:', error);
    }
  }

  const closeInvoiceModal = () => {
    setShowInvoiceAddressModal(false);
    setInvoiceType(null);
    setInvoiceAddress(null);
  };

  const getPgdPdf = async () => {
    try {
      setPgdLoading(true);
      
      const response = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/api/orders/pgd/${order?._id}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/pdf',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch PDF');
      }

      // Get the blob from response
      const blob = await response.blob();

      // Create a URL for the blob and open in new tab
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');

      message.success('PGD PDF downloaded successfully');

    } catch (error) {
      console.error('Error downloading PGD PDF:', error);
      message.error('Failed to download PGD PDF');
    } finally {
      setPgdLoading(false);
    }
  };

  const transferTarget = getTransferTarget(order);
  const showTransferAction = canTransferOrderLocation(order, canTransfer);
  const showResetAction = canResetProcessedOrder(order, canReset);
  const showSpecialDeliveryAction = canSetSpecialDelivery(order, canEdit);
  const escalateActionTypes = ESCALATION_TYPE_KEYS.filter((type) =>
    hasPermission([ESCALATION_TYPES[type].escalatePermission])
    && !openEscalations.some((escalation) => escalation.type === type)
  );
  const escalationModalConfig = escalationModalType ? ESCALATION_TYPES[escalationModalType] : null;
  // Only open escalations are on this page, so a status change here is always a
  // resolve / close: drop it, which brings back that type's Escalate action.
  const escalationActions = useEscalationActions({
    onStatusChange: (escalation, note) => {
      onLocationTransferred?.(null, note);
      setOpenEscalations?.((current) => current.filter((open) => open._id !== escalation._id));
    },
    onReply: (escalation) => setOpenEscalations?.((current) => current.map((open) => (
      open._id === escalation._id ? escalation : open
    ))),
  });
  const escalationItems = [
    ...escalateActionTypes.map((type) => ({
      key: `escalate-${type}`,
      icon: <AlertOutlined />,
      label: ESCALATION_TYPES[type].escalateLabel,
      tone: "danger",
      onClick: () => setEscalationModalType(type),
    })),
    ...openEscalations.flatMap((escalation) => {
      const config = ESCALATION_TYPES[escalation.type];
      if (!config) return [];
      const items = [];
      if (hasPermission([config.viewPermission])) {
        items.push({
          key: `replies-${escalation._id}`,
          icon: <MessageOutlined />,
          label: `${config.name} Replies (${countReplies(escalation)})`,
          onClick: () => escalationActions.openActivity(escalation, order?.orderId),
        });
      }
      if (hasPermission([config.resolvePermission])) {
        items.push({
          key: `resolve-${escalation._id}`,
          icon: <CheckCircleOutlined />,
          label: `${config.resolveLabel} ${config.name} Escalation`,
          onClick: () => escalationActions.openResolve(escalation, order?.orderId),
        });
      }
      return items;
    }),
  ];
  const showRegenerateLabel = canRegenerateShippingLabel(order, canSendToCourier);

  const regenerateShippingLabel = async () => {
    try {
      setLabelLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_ENDPOINT}/api/orders/${order?._id}/shipping-label/regenerate`,
        {
          method: "GET",
          credentials: "include",
          headers: { Accept: "application/pdf" },
        }
      );

      const contentType = response.headers.get("content-type") || "";
      if (!response.ok || contentType.includes("application/json")) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || data.error || "Failed to regenerate the shipping label");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const disposition = response.headers.get("content-disposition") || "";
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
      link.href = url;
      link.download = filenameMatch?.[1] || `shipping_label_${order?.orderId || "order"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return { success: true, message: "Shipping label downloaded" };
    } catch (error) {
      return {
        success: false,
        message: error.message || "Failed to regenerate the shipping label",
      };
    } finally {
      setLabelLoading(false);
    }
  };
  const resetCarrierCopy = getTransferCarrierCopy(order);

  const handleTransferLocation = async () => {
    if (!transferTarget) {
      return { success: false, message: "No transfer target is available for this order." };
    }

    try {
      setTransferLoading(true);
      const res = await post(`api/orders/${order._id}/transfer-location`, {
        location: transferTarget.location,
        fulfilmentBy: transferTarget.fulfilmentBy,
      });
      if (res.success) {
        onLocationTransferred?.(res.order, res.note);
        return {
          success: true,
          message: res.message || "Order location transferred",
        };
      }
      return {
        success: false,
        message: res.error || "Failed to transfer order location",
      };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.error ||
          "Failed to transfer order location",
      };
    } finally {
      setTransferLoading(false);
    }
  };

  const handleResetOrder = async () => {
    try {
      setResetLoading(true);
      const res = await post(`api/orders/${order._id}/reset-courier`);
      if (res.success) {
        onLocationTransferred?.(res.order, res.note);
        return {
          success: true,
          message: res.message || "Order reset to Ready",
        };
      }
      return {
        success: false,
        message: res.error || "Failed to reset order",
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.error || "Failed to reset order",
      };
    } finally {
      setResetLoading(false);
    }
  };

  const handleSpecialDelivery = async () => {
    try {
      setSpecialDeliveryLoading(true);
      const res = await post(`api/orders/${order._id}/special-delivery`);
      if (res.success) {
        onLocationTransferred?.(res.order, res.note);
        return {
          success: true,
          message: res.message || "Order set to Special Delivery",
        };
      }
      return {
        success: false,
        message: res.error || "Failed to set special delivery",
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.error || "Failed to set special delivery",
      };
    } finally {
      setSpecialDeliveryLoading(false);
    }
  };

  const handleEscalate = async (reason) => {
    try {
      setEscalationLoading(true);
      const res = await post(escalationModalConfig.api.escalate(order._id), { reason });
      if (res.success) {
        onLocationTransferred?.(null, res.note);
        if (res.openEscalations) setOpenEscalations?.(res.openEscalations);
        return {
          success: true,
          message: res.message,
        };
      }
      return {
        success: false,
        message: res.error || "Failed to escalate order",
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.error || "Failed to escalate order",
      };
    } finally {
      setEscalationLoading(false);
    }
  };

  return (
    <div className="card rounded-4">
      <OrderHeader
        order={order}
        openCases={openCases}
        openEscalations={openEscalations}
        assessment={assessment}
        canEdit={canEdit}
        canStatusEdit={canStatusEdit}
        getOrderInvoice={getOrderInvoice}
        isLoading={isLoading}
        showInvoiceAddressModal={showInvoiceAddressModal}
        setShowInvoiceAddressModal={setShowInvoiceAddressModal}
        invoiceType={invoiceType}
        setInvoiceType={setInvoiceType}
        invoiceAddress={invoiceAddress}
        setInvoiceAddress={setInvoiceAddress}
        closeInvoiceModal={closeInvoiceModal}
        orderStatusChange={orderStatusChange}
        handleTrackOrder={handleTrackOrder}
        canReplacement={canReplacement}
        canAlternativeStock={canAlternativeStock}
        onOpenReplacement={() => setIsReplacementModalOpen(true)}
        onOpenAlternativeStock={() => setIsAlternativeStockModalOpen(true)}
        onOpenVerification={() => setIsVerificationModalOpen(true)}
        idCheckSummary={idCheckSummary}
        canMarkVerified={canMarkVerified}
        existingReplacements={existingReplacements}
        estimatedDelivery={trackingEstimatedDelivery || getServiceEstimatedDelivery(order)}
        canReturn={canReturn}
        existingReturn={existingReturn}
        returnLoading={returnLoading}
        onReturnClick={handleReturnClick}
        showTransferAction={showTransferAction}
        onOpenTransferModal={() => setShowTransferModal(true)}
        showResetAction={showResetAction}
        onOpenResetModal={() => setShowResetModal(true)}
        showSpecialDeliveryAction={showSpecialDeliveryAction}
        onOpenSpecialDeliveryModal={() => setShowSpecialDeliveryModal(true)}
        escalationItems={escalationItems}
        showRegenerateLabel={showRegenerateLabel}
        labelLoading={labelLoading}
        onRegenerateLabel={() => setShowRegenerateModal(true)}
        onOpenAddTracking={() => setIsAddTrackingModalOpen(true)}
        showAwaitingReplyAction={showAwaitingReplyAction}
        awaitingReply={Boolean(order?.awaitingReply)}
        awaitingReplyLoading={awaitingReplyLoading}
        onToggleAwaitingReply={handleAwaitingReply}
        onOpenDispatchDate={() => setIsDispatchDateModalOpen(true)}
      />

      <div className="card-body p-7">
        <div className="row g-3 mb-4 order-info-row">
          <CustomerInfo buyerInfo={order?.buyerInfo} customer={customer} />
          <DeliveryInfo
            shippingInfo={order?.shippingInfo}
            canEdit={canEdit}
            orderId={order?._id}
            isCarehome={order?.isCarehome}
          />
          <AssessmentLogs
            order={order}
            assessment={assessment}
            mailLogs={mailLogs}
            canAutomation={canAutomation}
            reminder={reminder}
            reminderLoading={reminderLoading}
            handleReminderChange={handleReminderChange}
            getPgdPdf={getPgdPdf}
            pgdLoading={pgdLoading}
          />
          <IdCheckStatusPanel idCheckSummary={idCheckSummary} />
        </div>

        {/* Order Logistics Section - Collapsible */}
        <OrderLogistics order={order} />
      </div>

      {/* Tracking Modal */}
      <TrackingModal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        loading={modalLoading}
        data={modalData}
        orderInfo={modalData?.orderInfo}
      />

      {/* Replacement Modal */}
      <ReplacementModal
        open={isReplacementModalOpen}
        onCancel={() => setIsReplacementModalOpen(false)}
        onSuccess={handleReplacementSuccess}
        order={order}
      />
      <AlternativeStockModal
        open={isAlternativeStockModalOpen}
        onCancel={() => setIsAlternativeStockModalOpen(false)}
        onSuccess={(invoices) => setOrder((prev) => ({ ...prev, invoices }))}
        order={order}
      />
      {/* QR Code Return Modal */}
      <ReturnQrModal
        open={isReturnModalOpen}
        onCancel={() => setIsReturnModalOpen(false)}
        returnData={existingReturn}
        onCreate={handleReturnCreate}
        creating={returnLoading}
      />
      {/* Manual Tracking Modal */}
      <AddTrackingModal
        open={isAddTrackingModalOpen}
        onCancel={() => setIsAddTrackingModalOpen(false)}
        onSuccess={handleTrackingAdded}
        order={order}
      />
      {/* Scheduled Dispatch Date Modal */}
      <DispatchDateModal
        open={isDispatchDateModalOpen}
        onCancel={() => setIsDispatchDateModalOpen(false)}
        onSuccess={handleDispatchDateSaved}
        order={order}
      />
      {/* ID Verification Modal */}
      <IdVerificationModal
        open={isVerificationModalOpen}
        onClose={() => setIsVerificationModalOpen(false)}
        orderId={order?._id}
        idCheckSummary={idCheckSummary}
        customer={customer}
        onSuccess={({ customer: updatedCustomer, idCheckSummary: summary }) => {
          if (summary && setIdCheckSummary) setIdCheckSummary(summary);
          if (updatedCustomer && setCustomer) setCustomer(updatedCustomer);
        }}
      />
      <TransferLocationModal
        open={showTransferModal}
        loading={transferLoading}
        currentLocation={order?.location || "MI Health Longcroft House"}
        currentFulfilment={order?.fulfilmentBy}
        willCancelLabel={order?.status === "PROCESSED"}
        willRestoreStock={order?.status === "PROCESSED" && orderHasInvoiceRecords(order)}
        labelOrder={{
          isPrime: order?.isPrime,
          isShipPlus: order?.isShipPlus,
          carrier: order?.carrier,
        }}
        target={transferTarget}
        onCancel={() => setShowTransferModal(false)}
        onConfirm={handleTransferLocation}
      />
      <ResetOrderModal
        open={showResetModal}
        loading={resetLoading}
        carrierCopy={resetCarrierCopy}
        isShipped={order?.status === "SHIPPED"}
        willRestoreStock={orderHasInvoiceRecords(order)}
        onCancel={() => setShowResetModal(false)}
        onConfirm={handleResetOrder}
      />
      <SpecialDeliveryModal
        open={showSpecialDeliveryModal}
        loading={specialDeliveryLoading}
        currentPostageCode={order?.postageCode}
        onCancel={() => setShowSpecialDeliveryModal(false)}
        onConfirm={handleSpecialDelivery}
      />
      <EscalationModal
        open={!!escalationModalConfig}
        loading={escalationLoading}
        title={escalationModalConfig?.escalateLabel || ""}
        subtitle={order?.orderId}
        description={escalationModalConfig?.escalateDescription}
        placeholder="Reason for escalation (required)"
        textRequired
        confirmLabel="Confirm Escalation"
        onCancel={() => setEscalationModalType(null)}
        onConfirm={handleEscalate}
      />
      {escalationActions.modals}
      <RegenerateLabelModal
        open={showRegenerateModal}
        loading={labelLoading}
        orderId={order?.orderId}
        carrierName={resetCarrierCopy.carrierName}
        onCancel={() => setShowRegenerateModal(false)}
        onConfirm={regenerateShippingLabel}
      />
    </div>
  );
};

const CollapsibleSection = ({ title, defaultExpanded = false, children }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className="mt-4 pt-4 border-top">
      <button
        className="btn btn-link text-decoration-none p-0 d-flex align-items-center w-100 text-start"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          border: 'none',
          background: 'none',
          transition: 'all 0.2s ease'
        }}
      >
        <i
          className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} me-3 text-muted`}
          style={{
            fontSize: '14px',
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        ></i>
        <span className="text-dark fw-bold" style={{ fontSize: '16px' }}>{title}</span>
        <span className="ms-auto text-muted" style={{ fontSize: '11px', fontWeight: '500' }}>
          {isExpanded ? 'Collapse' : 'Expand'}
        </span>
      </button>

      <div
        style={{
          height: isExpanded ? 'auto' : '0',
          overflow: 'hidden',
          transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          opacity: isExpanded ? 1 : 0,
          transform: isExpanded ? 'translateY(0)' : 'translateY(-10px)'
        }}
      >
        <div className="mt-4">
          {children}
        </div>
      </div>
    </div>
  );
};

const InfoCard = ({ label, value, icon }) => (
  <div className="col-lg-4 col-md-6 col-sm-6">
    <div
      className="p-4 rounded-3 h-100"
      style={{
        background: "linear-gradient(135deg, #f8f9fa 0%, #fff 100%)",
        border: "1px solid #e9ecef",
        transition: 'transform 0.2s ease, box-shadow 0.2s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '';
      }}
    >
      <div className="d-flex align-items-center justify-content-between">
        <div style={{ flex: 1 }}>
          <small
            className="text-muted text-uppercase d-block mb-2"
            style={{
              fontSize: "10px",
              letterSpacing: "0.8px",
              fontWeight: "600"
            }}
          >
            {label}
          </small>
          <div
            className="fw-bold text-dark"
            style={{ fontSize: "15px" }}
            title={value || "—"}
          >
            {value || "—"}
          </div>
        </div>
        <div
          className="rounded-circle d-flex align-items-center justify-content-center"
          style={{
            width: "42px",
            height: "42px",
            background: "rgba(108, 117, 125, 0.1)",
            flexShrink: 0
          }}
        >
          <i className={`${icon} text-secondary`} style={{ fontSize: "16px" }}></i>
        </div>
      </div>
    </div>
  </div>
);

const OrderLogistics = ({ order }) => {
  const logisticsData = [
    {
      label: "Location",
      value: order?.location,
      icon: "fas fa-map-marker-alt"
    },
    {
      label: "Fulfillment",
      value: order?.fulfilmentBy,
      icon: "fas fa-warehouse"
    },
    {
      label: "Format",
      value: order?.weightFormat,
      icon: "fas fa-box"
    },
    {
      label: "Service",
      value: order?.postageCode,
      icon: "fas fa-shipping-fast"
    },
    {
      label: "Service Description",
      value: order?.postageCodeDesc,
      icon: "fas fa-info-circle"
    },
    {
      label: "Est. Cost",
      value: order?.approximatePostageCost ? `£${order.approximatePostageCost}` : null,
      icon: "fas fa-pound-sign"
    },
    {
      label: "Shipped Date",
      value: getShippedDate(order) ? DateFormater(getShippedDate(order)) : null,
      icon: "fas fa-calendar-check"
    }
  ];

  return (
    <CollapsibleSection
      title="Logistics Details"
      defaultExpanded={false}
    >
      <div className="row g-3">
        {logisticsData.map((item, index) => (
          <InfoCard
            key={index}
            label={item.label}
            value={item.value}
            icon={item.icon}
          />
        ))}
      </div>
    </CollapsibleSection>
  );
};

// Royal Mail work (replacements, returns) is only valid once an order has gone out.
const ELIGIBLE_STATUSES = ['SHIPPED', 'DELIVERED'];

const hasCompleteShippingInfo = (order) =>
  Boolean(order?.shippingInfo?.addressLine1 && order?.shippingInfo?.city && order?.shippingInfo?.zip);

const fetchTrackingDetails = (trackingNumber, order) =>
  determineCarrier(trackingNumber, order) === 'AMAZON'
    ? get(`api/tracking/amazon/${trackingNumber}`)
    : get(`api/tracking/single/${trackingNumber}`);

/**
 * Helper function to determine replacement button visibility and state
 * @returns {object} { show: boolean, disabled: boolean, reason: string }
 */
const getReplacementButtonState = (order, existingReplacements, canReplacement) => {
  // No permission - hide button
  if (!canReplacement) {
    return { show: false, disabled: false, reason: 'No permission' };
  }

  // Check if order status is eligible for replacement
  const isEligibleStatus = ELIGIBLE_STATUSES.includes(order?.status);

  // Check if replacement already exists
  const hasExistingReplacement = existingReplacements && existingReplacements.length > 0;

  // If replacement exists - show DISABLED button (user can see feature but can't use it)
  if (hasExistingReplacement) {
    const latestReplacement = existingReplacements[0];
    return {
      show: true,
      disabled: true,
      reason: `Replacement already issued (${latestReplacement.royalMailOrderRef || 'Ref N/A'})`
    };
  }

  // If order status not eligible - HIDE button
  if (!isEligibleStatus) {
    return {
      show: false,
      disabled: false,
      reason: `Order must be SHIPPED or DELIVERED (current: ${order?.status || 'UNKNOWN'})`
    };
  }

  // Check shipping info completeness
  if (!hasCompleteShippingInfo(order)) {
    return {
      show: false,
      disabled: false,
      reason: 'Incomplete shipping information'
    };
  }

  // All checks passed - show ENABLED button
  return { show: true, disabled: false, reason: 'Eligible for replacement' };
};

const ALTERNATIVE_STOCK_STATUSES = ['PROCESSED', 'SHIPPED'];

const getAlternativeStockButtonState = (order, canAlternativeStock) => {
  if (!canAlternativeStock || !ALTERNATIVE_STOCK_STATUSES.includes(order?.status)) {
    return { show: false, disabled: false, reason: '' };
  }
  if (orderHasAnyInvoices(order) && order?.status !== 'SHIPPED') {
    return {
      show: true,
      disabled: true,
      reason: 'Stock has already been deducted for this order',
    };
  }
  return { show: true, disabled: false, reason: '' };
};

const getReturnButtonState = (order, existingReturn, canReturn) => {
  if (!canReturn) {
    return { show: false, reason: 'No permission' };
  }

  // A return is chargeable, so it is created once - the button then just
  // re-opens the QR code and label that were already generated.
  if (existingReturn) {
    return {
      show: true,
      reason: `Return already created${existingReturn.trackingNumber ? ` (${existingReturn.trackingNumber})` : ''} - view QR code and label`,
    };
  }

  if (!ELIGIBLE_STATUSES.includes(order?.status)) {
    return {
      show: false,
      reason: `Order must be SHIPPED or DELIVERED (current: ${order?.status || 'UNKNOWN'})`,
    };
  }

  if (!hasCompleteShippingInfo(order)) {
    return { show: false, reason: 'Incomplete shipping information' };
  }

  return { show: true, reason: 'Create a Royal Mail QR code return' };
};

// Sub-components
const BRAND = '#DB814F';
const BRAND_LIGHT = '#fff5ef';
const BRAND_BORDER = '#f5cdb3';

const InvoiceOptionCard = ({ selected, onClick, icon, label, desc }) => (
  <div
    onClick={onClick}
    style={{
      border: `2px solid ${selected ? BRAND : '#e8e8e8'}`,
      borderRadius: 10,
      padding: '14px 16px',
      cursor: 'pointer',
      background: selected ? BRAND_LIGHT : '#fafafa',
      transition: 'border-color 0.18s, background 0.18s',
      userSelect: 'none',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 4 }}>
      <span
        style={{
          width: 30, height: 30, borderRadius: 6,
          background: selected ? BRAND : '#ebebeb',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, transition: 'background 0.18s',
        }}
      >
        <i className={`fa ${icon}`} style={{ color: selected ? '#fff' : '#aaa', fontSize: 13 }} />
      </span>
      <span style={{ fontWeight: 600, fontSize: 13, color: selected ? BRAND : '#333' }}>
        {label}
      </span>
      {selected && (
        <span style={{ marginLeft: 'auto', color: BRAND, fontSize: 15, lineHeight: 1 }}>✓</span>
      )}
    </div>
    <p style={{ margin: 0, fontSize: 11, color: '#999', paddingLeft: 39 }}>{desc}</p>
  </div>
);

const OrderHeader = ({
  order,
  openCases,
  openEscalations,
  assessment,
  canEdit,
  canStatusEdit,
  getOrderInvoice,
  isLoading,
  showInvoiceAddressModal,
  setShowInvoiceAddressModal,
  invoiceType,
  setInvoiceType,
  invoiceAddress,
  setInvoiceAddress,
  closeInvoiceModal,
  orderStatusChange,
  handleTrackOrder,
  canReplacement,
  canAlternativeStock,
  onOpenReplacement,
  onOpenAlternativeStock,
  onOpenVerification,
  idCheckSummary,
  canMarkVerified,
  existingReplacements,
  estimatedDelivery,
  canReturn,
  existingReturn,
  returnLoading,
  onReturnClick,
  showTransferAction,
  onOpenTransferModal,
  showResetAction,
  onOpenResetModal,
  showSpecialDeliveryAction,
  onOpenSpecialDeliveryModal,
  escalationItems,
  showRegenerateLabel,
  labelLoading,
  onRegenerateLabel,
  onOpenAddTracking,
  onOpenDispatchDate,
  showAwaitingReplyAction,
  awaitingReply,
  awaitingReplyLoading,
  onToggleAwaitingReply,
}) => {
  const replacementButtonState = getReplacementButtonState(order, existingReplacements, canReplacement);
  const alternativeStockButtonState = getAlternativeStockButtonState(order, canAlternativeStock);
  const returnButtonState = getReturnButtonState(order, existingReturn, canReturn);
  const canGenerate = invoiceType && invoiceAddress;

  return (
    <header className="card-header bg-transparent p-7">
      <div className="order-detail-header">
        <div className="order-header-meta">
          <OrderMetaData
            order={order}
            orderStatusChange={orderStatusChange}
            canStatusEdit={canStatusEdit}
            handleTrackOrder={handleTrackOrder}
            existingReplacements={existingReplacements}
            estimatedDelivery={estimatedDelivery}
            openCases={openCases}
            openEscalations={openEscalations}
          />
        </div>

        <OrderStatusStrip
          order={order}
          assessment={assessment}
          showIdStatus={
            idCheckSummary?.required ||
            (idCheckSummary?.productsRequireIdCheck && idCheckSummary?.isVerified)
          }
          isVerified={idCheckSummary?.isVerified}
        />

        <div className="order-header-side">
            <div className="order-header-actions">
            {order?.awaitingReply && (
              <span
                className="order-awaiting-reply"
                title={
                  order.awaitingReplyAt
                    ? `Waiting for a customer reply since ${DateTimeFormater(order.awaitingReplyAt)}`
                    : "Waiting for a customer reply"
                }
              >
                Awaiting reply
              </span>
            )}
            <OrderStatus
              status={order?.status}
              isPGD={order?.isPGD}
              orderStatusChange={orderStatusChange}
              canStatusEdit={canStatusEdit}
            />
            <OrderActionToolbar
              order={order}
              assessment={assessment}
              isLoading={isLoading}
              idCheckRequired={idCheckSummary?.required}
              canMarkVerified={canMarkVerified}
              isVerified={idCheckSummary?.isVerified}
              replacementButtonState={replacementButtonState}
              alternativeStockButtonState={alternativeStockButtonState}
              onOpenAlternativeStock={onOpenAlternativeStock}
              returnButtonState={returnButtonState}
              returnLoading={returnLoading}
              onOpenVerification={onOpenVerification}
              onOpenReplacement={onOpenReplacement}
              onReturnClick={onReturnClick}
              canAddTracking={canEdit}
              onOpenAddTracking={onOpenAddTracking}
              showAwaitingReplyAction={showAwaitingReplyAction}
              awaitingReply={awaitingReply}
              awaitingReplyLoading={awaitingReplyLoading}
              onToggleAwaitingReply={onToggleAwaitingReply}
              showDispatchDate={canEdit && canScheduleDispatch(order)}
              onOpenDispatchDate={onOpenDispatchDate}
              onOpenInvoiceModal={() => setShowInvoiceAddressModal(true)}
              showTransferAction={showTransferAction}
              onOpenTransferModal={onOpenTransferModal}
              showResetAction={showResetAction}
              onOpenResetModal={onOpenResetModal}
              showSpecialDeliveryAction={showSpecialDeliveryAction}
              onOpenSpecialDeliveryModal={onOpenSpecialDeliveryModal}
              escalationItems={escalationItems}
              showRegenerateLabel={showRegenerateLabel}
              labelLoading={labelLoading}
              onRegenerateLabel={onRegenerateLabel}
            />
            </div>
          <Modal
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span
                  style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: BRAND_LIGHT, border: `1px solid ${BRAND_BORDER}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <FilePdfOutlined style={{ color: BRAND, fontSize: 16 }} />
                </span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>Download Invoice</div>
                  <div style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>Select type and address, then confirm</div>
                </div>
              </div>
            }
            open={showInvoiceAddressModal}
            onCancel={closeInvoiceModal}
            footer={null}
            width={440}
          >
            <div style={{ padding: '4px 0 8px' }}>

              {/* ── Invoice Type ── */}
              <div style={{ marginBottom: 20 }}>
                <p style={{
                  fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: '0.7px', color: '#aaa', margin: '0 0 10px',
                }}>
                  Invoice Type
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <InvoiceOptionCard
                    selected={invoiceType === 'b2b'}
                    onClick={() => setInvoiceType('b2b')}
                    icon="fa-building"
                    label="B2B Invoice"
                    desc="Flat 20% VAT on all items"
                  />
                  <InvoiceOptionCard
                    selected={invoiceType === 'normal'}
                    onClick={() => setInvoiceType('normal')}
                    icon="fa-file-alt"
                    label="Normal Invoice"
                    desc="VAT per product category"
                  />
                </div>
              </div>

              {/* ── Address ── */}
              <div style={{ marginBottom: 24 }}>
                <p style={{
                  fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: '0.7px', color: '#aaa', margin: '0 0 10px',
                }}>
                  Bill To
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <InvoiceOptionCard
                    selected={invoiceAddress === 'billing'}
                    onClick={() => setInvoiceAddress('billing')}
                    icon="fa-user"
                    label="Billing Address"
                    desc="Customer's billing details"
                  />
                  <InvoiceOptionCard
                    selected={invoiceAddress === 'shipping'}
                    onClick={() => setInvoiceAddress('shipping')}
                    icon="fa-truck"
                    label="Shipping Address"
                    desc="Delivery destination"
                  />
                </div>
              </div>

              {/* ── Confirm ── */}
              <Button
                type="primary"
                block
                size="large"
                icon={<FilePdfOutlined />}
                loading={isLoading}
                disabled={!canGenerate}
                onClick={() => getOrderInvoice(invoiceAddress, invoiceType)}
                style={{
                  background: canGenerate ? BRAND : undefined,
                  borderColor: canGenerate ? BRAND : undefined,
                  fontWeight: 600,
                  fontSize: 14,
                  height: 44,
                }}
              >
                Generate Invoice
              </Button>

              {!canGenerate && (
                <p style={{ textAlign: 'center', fontSize: 11, color: '#bbb', margin: '8px 0 0' }}>
                  {!invoiceType && !invoiceAddress
                    ? 'Select an invoice type and address to continue'
                    : !invoiceType
                    ? 'Select an invoice type to continue'
                    : 'Select an address to continue'}
                </p>
              )}
            </div>
          </Modal>
        </div>
      </div>
    </header>
  );
};

const ShipmentDetails = ({ order, estimatedDelivery }) => {
  const details = [
    { label: "Courier", value: getCarrierLabel(order) },
    { label: "Service", value: order?.postageCodeDesc || order?.postageCode },
    { label: "Est. Delivery", value: estimatedDelivery ? DateFormater(estimatedDelivery) : null },
  ].filter((detail) => detail.value);

  return details.map((detail) => (
    <React.Fragment key={detail.label}>
      <br />
      <small className="text-muted">
        {detail.label}: {detail.value}
      </small>
    </React.Fragment>
  ));
};

// Open eBay cases on this order's products: each badge opens the case, and the
// product name links to its eBay listing.
const OpenCaseBadges = ({ openCases }) => {
  const canViewCases = usePermissionCheck(['general.case_view']);
  if (!openCases || openCases.length === 0) return null;

  return (
    <div className="d-flex flex-column gap-1 mt-2">
      {openCases.map((openCase) => {
        const respondBy = openCase.respondByDate
          ? ` · respond by ${DateFormater(openCase.respondByDate)}`
          : '';
        const badge = (
          <span
            className="badge rounded-pill"
            style={{
              background: '#f8d7da',
              color: '#842029',
              border: '1px solid #f5c2c7',
              fontSize: '11px',
              padding: '5px 12px',
              fontWeight: '500',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}
            title={`Case #${openCase.inquiryId} · ${openCase.status}${respondBy}`}
          >
            <i className="fas fa-exclamation-circle" style={{ fontSize: '10px' }} />
            <span>Case open #{openCase.inquiryId}</span>
          </span>
        );

        return (
          <div key={openCase._id} className="d-flex align-items-center gap-2 flex-wrap">
            {canViewCases ? <Link to={`/cases/${openCase._id}`}>{badge}</Link> : badge}
            {openCase.productTitle && (
              openCase.itemUrl ? (
                <a
                  href={openCase.itemUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="small text-decoration-underline"
                  title={openCase.productTitle}
                >
                  {truncateText(openCase.productTitle, 40)}
                  <i className="fas fa-external-link-alt ms-1" style={{ fontSize: '9px' }} />
                </a>
              ) : (
                <small className="text-muted" title={openCase.productTitle}>
                  {truncateText(openCase.productTitle, 40)}
                </small>
              )
            )}
          </div>
        );
      })}
    </div>
  );
};

const EscalationBadge = ({ escalation }) => {
  const config = ESCALATION_TYPES[escalation.type];
  if (!config) return null;

  return (
    <span
      className="badge rounded-pill"
      style={{
        background: '#f8d7da',
        color: '#842029',
        border: '1px solid #f5c2c7',
        fontSize: '11px',
        padding: '5px 12px',
        fontWeight: '500',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px'
      }}
      title={`Escalated by ${escalation.escalatedBy || '—'} on ${DateTimeFormater(escalation.escalatedAt)} · ${escalation.reason || ''}`}
    >
      <AlertOutlined style={{ fontSize: '10px' }} />
      <span>{config.badgeLabel}</span>
    </span>
  );
};

const OrderMetaData = ({ order, orderStatusChange, canStatusEdit, handleTrackOrder, existingReplacements, openCases, openEscalations, estimatedDelivery }) => (
  <>
    <span className="d-inline-block">
      <i className="far fa-calendar me-3" />
      {DateFormater(order?.createdAt)}
    </span>
    <br />
    <small className="text-muted">{order?.orderId}</small>
    {order.isSignPost && (
      <>
        <br />
        <small className="text-danger">
          <i className="fas fa-exclamation-triangle me-2" />
          <strong>Repeat Offender</strong>
        </small>
      </>
    )}
    {order.trackingNumber && <TrackingInfo trackingNumber={order.trackingNumber} handleTrackOrder={handleTrackOrder} />}
    {getShippedDate(order) && (
      <>
        <br />
        <small className="text-muted">
          Shipped Date: {DateFormater(getShippedDate(order))}
        </small>
      </>
    )}
    <ShipmentDetails order={order} estimatedDelivery={estimatedDelivery} />
    <br />
    <div className="d-flex align-items-center gap-2 flex-wrap mt-1">
      {isDispatchScheduledForLater(order) && canScheduleDispatch(order) && (
        <span
          className="badge rounded-pill ms-2"
          style={{
            background: '#fff3cd',
            color: '#856404',
            border: '1px solid #ffc107',
            fontSize: '11px',
            padding: '5px 12px',
            fontWeight: '500',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px'
          }}
          title="This order is held back from the courier until the scheduled dispatch date"
        >
          <i className="fas fa-clock" style={{ fontSize: '10px' }} />
          <span>
            Dispatch Delayed: {DateFormater(order.scheduledDispatchDate)}
            {' · '}
            {formatDaysUntilDispatch(order)}
          </span>
        </span>
      )}
      {existingReplacements && existingReplacements.length > 0 && (
        <span
          className="badge rounded-pill ms-2"
          style={{
            background: '#fff3cd',
            color: '#856404',
            border: '1px solid #ffc107',
            fontSize: '11px',
            padding: '5px 12px',
            fontWeight: '500',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px'
          }}
          title={`Royal Mail Ref: ${existingReplacements[0].royalMailOrderRef || '—'}`}
        >
          <i className="fas fa-redo" style={{ fontSize: '10px' }} />
          <span>Replacement Issued</span>
        </span>
      )}
      {openEscalations?.map((escalation) => (
        <EscalationBadge key={escalation._id} escalation={escalation} />
      ))}
    </div>
    <OpenCaseBadges openCases={openCases} />
  </>
);

const indicatorDefinitions = {
  Green: "Assessment is correct. No history of possible contraindications.",
  Amber: "Possibility of contraindications, needs a drug review.",
  Red: "Customer has ordered the same item again repeatedly before the allowable day limit. Possible assessment answer change.",
};

const indicatorTooltipClass = {
  Green: "indicator-tooltip-green",
  Amber: "indicator-tooltip-amber",
  Red: "indicator-tooltip-red",
};

const OrderIndicator = ({ order }) => {
  // Determine the class for each light
  const getClass = (color) => {
    return `light ${color.toLowerCase()} ${order.indicator === color ? "on" : ""
      }`;
  };

  const tooltipText =  order.indicatorReason || indicatorDefinitions[order.indicator] || "";
  const tooltipClass = indicatorTooltipClass[order.indicator] || "";

  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip className={tooltipClass}>
          {tooltipText}
        </Tooltip>
      }
    >
      <div
        className="traffic-light"
        style={{ cursor: 'pointer' }}
        aria-label={`Order indicator: ${order.indicator || 'Unknown'}`}
      >
        <span className={getClass("Red")}></span>
        <span className={getClass("Amber")}></span>
        <span className={getClass("Green")}></span>
      </div>
    </OverlayTrigger>
  );
};

const OrderStatusStrip = ({ order, assessment, showIdStatus, isVerified }) => (
  <div className="order-status-strip">
    {order?.anomalyVisible && assessment?._id && (
      <AnomalyIconButton assessmentId={assessment._id} reason={order.indicatorReason} />
    )}
    {showIdStatus && <IdStatusIcon isVerified={isVerified} />}
    <OrderIndicator order={order} />
  </div>
);

const AnomalyIconButton = ({ assessmentId, reason }) => {
  const tooltipText =
    reason || "Customer answered one or more assessment questions more than once";

  return (
    <AntTooltip title={tooltipText}>
      <a
        href={`/assessment/tracker/${assessmentId}`}
        target="_blank"
        rel="noreferrer"
        aria-label="Assessment answer changed — view tracker"
        style={{ display: "inline-flex", alignItems: "center" }}
      >
        <i className="fa fa-circle text-danger-glow-red blink" aria-hidden="true" />
      </a>
    </AntTooltip>
  );
};

const IdStatusIcon = ({ isVerified }) => {
  if (isVerified) {
    return (
      <AntTooltip title="ID verified">
        <span className="id-status-icon-btn id-status-icon-btn--verified" aria-label="ID verified">
          <SafetyCertificateOutlined className="id-status-icon-btn__icon" />
        </span>
      </AntTooltip>
    );
  }

  return (
    <AntTooltip title="ID not verified — photo ID still required">
      <span className="id-status-icon-btn id-status-icon-btn--pending" aria-label="ID not verified">
        <IdcardOutlined className="id-status-icon-btn__icon" />
        <span className="id-status-icon-btn__alert" aria-hidden="true">!</span>
      </span>
    </AntTooltip>
  );
};

const OrderActionItem = ({ icon, label, hint, tone, disabled, loading, href, onRun }) => {
  const className = [
    "order-action-item",
    tone ? `is-${tone}` : "",
    disabled ? "is-disabled" : "",
  ].filter(Boolean).join(" ");

  const body = (
    <>
      <span className="order-action-item__icon">{loading ? <LoadingOutlined /> : icon}</span>
      <span className="order-action-item__copy">
        <span className="order-action-item__label">{label}</span>
        {hint ? <span className="order-action-item__hint">{hint}</span> : null}
      </span>
    </>
  );

  if (href && !disabled) {
    return (
      <Link to={href} className={className} role="menuitem" onClick={onRun}>
        {body}
      </Link>
    );
  }

  return (
    <button
      type="button"
      className={className}
      role="menuitem"
      disabled={disabled || loading}
      onClick={onRun}
    >
      {body}
    </button>
  );
};

const OrderActionToolbar = ({
  order,
  assessment,
  isLoading,
  idCheckRequired,
  canMarkVerified,
  isVerified,
  replacementButtonState,
  alternativeStockButtonState,
  onOpenAlternativeStock,
  returnButtonState,
  returnLoading,
  onOpenVerification,
  onOpenReplacement,
  onReturnClick,
  canAddTracking,
  onOpenAddTracking,
  showAwaitingReplyAction,
  awaitingReply,
  awaitingReplyLoading,
  onToggleAwaitingReply,
  showDispatchDate,
  onOpenDispatchDate,
  onOpenInvoiceModal,
  showTransferAction,
  onOpenTransferModal,
  showResetAction,
  onOpenResetModal,
  showSpecialDeliveryAction,
  onOpenSpecialDeliveryModal,
  escalationItems,
  showRegenerateLabel,
  labelLoading,
  onRegenerateLabel,
}) => {
  const [open, setOpen] = useState(false);
  const showAssessmentActions = order?.type === "P-Med" && assessment;
  const busy = isLoading || labelLoading || returnLoading;

  const closeAndRun = (action) => {
    setOpen(false);
    action?.();
  };

  const review = [];
  if (showAssessmentActions) {
    review.push(
      {
        key: "assessment",
        icon: <FileSearchOutlined />,
        label: "See Assessment",
        tone: "primary",
        href: `/questionnaire/${assessment._id}`,
      },
      {
        key: "questionnaire",
        icon: <FormOutlined />,
        label: "Questionnaire",
        href: `/ebay/question/demo/${assessment._id}`,
      }
    );
  }

  const orderActions = [];
  if (idCheckRequired && canMarkVerified && !isVerified) {
    orderActions.push({
      key: "id",
      icon: <CheckCircleOutlined />,
      label: "Mark ID verified",
      onClick: onOpenVerification,
    });
  }
  if (showAwaitingReplyAction) {
    orderActions.push({
      key: "awaiting-reply",
      icon: <MessageOutlined />,
      label: awaitingReply ? "Unmark awaiting reply" : "Mark awaiting reply",
      loading: awaitingReplyLoading,
      hint: awaitingReply ? "Waiting for a customer reply" : "",
      onClick: onToggleAwaitingReply,
    });
  }
  if (showTransferAction) {
    orderActions.push({
      key: "transfer",
      icon: <SwapOutlined />,
      label: "Transfer Location",
      onClick: onOpenTransferModal,
    });
  }
  if (showResetAction) {
    orderActions.push({
      key: "reset",
      icon: <UndoOutlined />,
      label: "Reset to Ready",
      onClick: onOpenResetModal,
    });
  }
  if (showSpecialDeliveryAction) {
    orderActions.push({
      key: "special",
      icon: <ThunderboltOutlined />,
      label: "Special Delivery",
      onClick: onOpenSpecialDeliveryModal,
    });
  }
  orderActions.push({
    key: "invoice",
    icon: <FilePdfOutlined />,
    label: "Download Invoice",
    loading: isLoading,
    onClick: onOpenInvoiceModal,
  });
  if (showRegenerateLabel) {
    orderActions.push({
      key: "label",
      icon: <PrinterOutlined />,
      label: "Regenerate Shipping Label",
      loading: labelLoading,
      onClick: onRegenerateLabel,
    });
  }
  if (canAddTracking) {
    orderActions.push({
      key: "tracking",
      icon: <CarOutlined />,
      label: "Add Tracking",
      onClick: onOpenAddTracking,
    });
  }
  if (showDispatchDate) {
    orderActions.push({
      key: "dispatch",
      icon: <CalendarOutlined />,
      label: "Dispatch Date",
      onClick: onOpenDispatchDate,
    });
  }
  if (alternativeStockButtonState.show) {
    orderActions.push({
      key: "alternative-stock",
      icon: <ShoppingOutlined />,
      label: "Use Alternative Stock",
      disabled: alternativeStockButtonState.disabled,
      hint: alternativeStockButtonState.disabled ? alternativeStockButtonState.reason : "",
      onClick: onOpenAlternativeStock,
    });
  }
  if (replacementButtonState.show) {
    orderActions.push({
      key: "replace",
      icon: <RedoOutlined />,
      label: "Replace Order",
      tone: "warn",
      disabled: replacementButtonState.disabled,
      hint: replacementButtonState.disabled ? replacementButtonState.reason : "",
      onClick: onOpenReplacement,
    });
  }
  if (returnButtonState.show) {
    orderActions.push({
      key: "return",
      icon: <QrcodeOutlined />,
      label: "QR Return",
      loading: returnLoading,
      hint: returnButtonState.reason || "",
      onClick: onReturnClick,
    });
  }

  const sections = [
    review.length ? { title: "Review", items: review } : null,
    orderActions.length ? { title: "Order", items: orderActions } : null,
    escalationItems.length ? { title: "Escalations", items: escalationItems } : null,
  ].filter(Boolean);

  return (
    <div className="order-action-toolbar">
      <Dropdown
        open={open}
        onOpenChange={setOpen}
        trigger={["click"]}
        placement="bottomRight"
        overlayClassName="order-action-dropdown"
        dropdownRender={() => (
          <div className="order-action-menu" role="menu">
            {sections.map((section) => (
              <div key={section.title} className="order-action-menu__section">
                <div className="order-action-menu__label">{section.title}</div>
                {section.items.map((item) => (
                  <OrderActionItem
                    key={item.key}
                    icon={item.icon}
                    label={item.label}
                    hint={item.hint}
                    tone={item.tone}
                    disabled={item.disabled}
                    loading={item.loading}
                    href={item.href}
                    onRun={() => {
                      if (item.disabled || item.loading) return;
                      if (item.href) {
                        setOpen(false);
                        return;
                      }
                      closeAndRun(item.onClick);
                    }}
                  />
                ))}
              </div>
            ))}
          </div>
        )}
      >
        <button
          type="button"
          className={`order-action-trigger${open ? " is-open" : ""}`}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          {busy ? <LoadingOutlined /> : <UnorderedListOutlined />}
          <span>Actions</span>
          <DownOutlined />
        </button>
      </Dropdown>
    </div>
  );
};

const TrackingInfo = ({ trackingNumber, handleTrackOrder }) => (
  <>
    <br />
    <small className="text-primary">
      Track Shipment:{" "}
      {trackingNumber ? (
        <span
          className="ms-1 text-decoration-underline cursor-pointer"
          style={{ cursor: 'pointer' }}
          onClick={() => handleTrackOrder(trackingNumber)}
        >
          {trackingNumber}
        </span>
      ) : (
        <span>No tracking number available</span>
      )}
    </small>
  </>
);

const OrderStatus = ({ status, isPGD, orderStatusChange, canStatusEdit }) => {
  const [editing, setEditing] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(status);

  const handleChange = async (e) => {
    const newStatus = e.target.value;
    setSelectedStatus(newStatus);
    setEditing(false);
    await orderStatusChange(newStatus);
  };

  const statusOptions = [
    { value: "NOT_STARTED", label: "ERROR" },
    { value: "PENDING", label: "PENDING P APPROVAL" },
    { value: "SEND_TO_RM", label: "READY FOR RM" },
    { value: "PROCESSED", label: "SENT TO RM" },
    { value: "SHIPPED", label: "DISPATCHED" },
    { value: "CANCELED", label: "CANCELED" },
    { value: "PRIME_READY", label: "PRIME_READY" },
    // Add all relevant statuses
  ];

  const statusView = getStatus(status, isPGD);

  return editing ? (
    <select
      className="order-status-select"
      value={selectedStatus}
      onChange={handleChange}
      onBlur={() => setEditing(false)}
      autoFocus
    >
      {statusOptions.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  ) : (
    <span className={`order-status-chip alert mb-0 border-0 ${statusView.class}`}>
      <span>{statusView.text}</span>
      {canStatusEdit && (
        <button
          type="button"
          className="order-status-chip__edit"
          onClick={() => setEditing(true)}
          title="Edit status"
          aria-label="Edit status"
        >
          <i className="fa fa-pen" aria-hidden="true" />
        </button>
      )}
    </span>
  );
};

const OrderInfoCard = ({ icon, title, action, children }) => (
  <div className="col-lg-4 col-md-6 col-12">
    <div className="order-info-card h-100">
      <div className="order-info-card__head">
        <h6 className="order-info-card__title">
          <i className={icon} aria-hidden="true" />
          {title}
        </h6>
        {action}
      </div>
      <div className="order-info-card__body">{children}</div>
    </div>
  </div>
);

const OrderInfoEditAction = ({ to, onClick, title }) => (
  <Link
    to={to}
    className="order-info-card__action"
    title={title}
    onClick={onClick}
  >
    <i className="fa fa-pen" aria-hidden="true" />
  </Link>
);

const CustomerInfo = ({ buyerInfo, customer }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleMarkAsRepeatOffender = async (reason) => {
    setLoading(true);
    try {
      const response = await post('api/dashboard/repeat-offenders', {
        name: buyerInfo?.fullname,
        customerId: buyerInfo?.username,
        reason,
      });

      if (response.success) {
        message.success(response.message);
      } else {
        message.error(response.error);
      }
    } catch (error) {
      message.error('Failed to mark customer as repeat offender');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <OrderInfoCard
        icon="fas fa-user"
        title="Customer"
        action={
          !customer && (
            <OrderInfoEditAction
              to="/orders/edit/"
              title="Mark as repeat offender"
              onClick={(e) => {
                e.preventDefault();
                setModalVisible(true);
              }}
            />
          )
        }
      >
        <p className="order-info-line order-info-line--primary">
          {buyerInfo?.fullname}
          {buyerInfo?.username ? ` (${buyerInfo.username})` : ""}
        </p>
        {buyerInfo?.email && (
          <p className="order-info-line">{buyerInfo.email}</p>
        )}
        {buyerInfo?.phone && (
          <p className="order-info-line">{buyerInfo.phone}</p>
        )}
      </OrderInfoCard>
      <RepeatOffenderModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onConfirm={handleMarkAsRepeatOffender}
        loading={loading}
      />
    </>
  );
};

const DeliveryInfo = ({ shippingInfo, canEdit, orderId, isCarehome }) => (
  <OrderInfoCard
    icon="fas fa-truck"
    title="Delivery address"
    action={
      canEdit && (
        <OrderInfoEditAction
          to={`/orders/edit/${orderId}`}
          title="Edit delivery address"
        />
      )
    }
  >
    {isCarehome && (
      <span className="order-info-badge order-info-badge--warning">
        <i className="fas fa-exclamation-triangle" aria-hidden="true" />
        Care home detected
      </span>
    )}
    <p className="order-info-line">
      {shippingInfo?.addressLine1}
      {shippingInfo?.addressLine2 ? `, ${shippingInfo.addressLine2}` : ""}
    </p>
    <p className="order-info-line text-capitalize">
      {shippingInfo?.city}
      {shippingInfo?.zip ? `, ${shippingInfo.zip.toUpperCase()}` : ""}
    </p>
    {shippingInfo?.country && (
      <p className="order-info-line text-uppercase">{shippingInfo.country}</p>
    )}
  </OrderInfoCard>
);

const AssessmentLogs = ({
  order,
  assessment,
  mailLogs,
  canAutomation,
  reminder,
  reminderLoading,
  handleReminderChange,
  getPgdPdf,
  pgdLoading
}) => (
  <OrderInfoCard
    icon="fas fa-clipboard-list"
    title="Assessment logs"
    action={
      order.type === "P-Med" && canAutomation ? (
        <label className="order-info-automation">
          <Switch
            loading={reminderLoading}
            checked={reminder === 1}
            onChange={(checked) => handleReminderChange(order?._id, checked ? 1 : 0)}
            size="small"
          />
          <span>Reminder automation</span>
        </label>
      ) : null
    }
  >
    <AssessmentLogList
      order={order}
      assessment={assessment}
      mailLogs={mailLogs}
      getPgdPdf={getPgdPdf}
      pgdLoading={pgdLoading}
    />
  </OrderInfoCard>
);

const buildAssessmentLogEntries = ({ mailLogs, assessment }) => {
  const entries = [];

  (mailLogs || []).forEach((log, index) => {
    let text;
    if (log.message === "ID_VERIFICATION_REQUEST") {
      text = "ID verification request sent";
    } else if (index < 2) {
      text = `${index + 1} / 2 Assessment Sent`;
    } else {
      text = "Questionnaire Sent";
    }

    entries.push({
      id: `mail-${index}-${log.mailSentAt || index}`,
      text,
      date: log.mailSentAt,
    });
  });

  if (assessment?.status === "COMPLETE") {
    const completedAt = assessment.completedAt || assessment.approvalTime;
    entries.push({
      id: "complete",
      text: completedAt
        ? `Assessment confirmed at ${DateTimeFormater(completedAt)}`
        : "Assessment Confirmed",
      embeddedDate: true,
    });
  }

  if (assessment?.isReview && assessment.approvalTime) {
    const reviewAt = assessment.approvalTime;
    entries.push({
      id: "review",
      text: `Assessment reviewed${assessment.reviewBy ? ` by ${assessment.reviewBy}` : ""}${reviewAt ? ` at ${DateTimeFormater(reviewAt)}` : ""}`,
      embeddedDate: true,
    });
  }

  return entries.sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1;
    if (!b.date) return -1;
    return new Date(a.date) - new Date(b.date);
  });
};

const AssessmentLogList = ({ order, assessment, mailLogs, getPgdPdf, pgdLoading }) => {
  const isEligible = order.type === "P-Med" || order.isPGD;
  if (!isEligible) {
    return (
      <p className="order-info-empty">Not applicable for this order type</p>
    );
  }

  const entries = buildAssessmentLogEntries({ mailLogs, assessment });

  return (
    <>
      {entries.length === 0 ? (
        <p className="order-info-empty">No assessment activity logged yet</p>
      ) : (
        <ul className="order-info-log-list">
          {entries.map((entry) => (
            <li key={entry.id} className="order-info-log-list__item">
              <span className="order-info-log-list__text">{entry.text}</span>
              {!entry.embeddedDate && entry.date && (
                <span className="order-info-log-list__time">
                  {DateTimeFormater(entry.date)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {order.isPGD && order.supplyBy && (
        <p className="order-info-log-pgd">
          Supply by:{" "}
          <button
            type="button"
            className="order-info-log-pgd__link"
            onClick={() => getPgdPdf()}
            disabled={pgdLoading}
          >
            {pgdLoading ? "Please wait..." : order.supplyBy}
          </button>
        </p>
      )}
    </>
  );
};

const TransferLocationModal = ({
  open,
  loading,
  currentLocation,
  currentFulfilment,
  willCancelLabel,
  willRestoreStock,
  labelOrder,
  target,
  onCancel,
  onConfirm,
}) => {
  const THEME = {
    primary: "#4e7661",
    light: "#eef4f1",
    border: "#c5d9ce",
    muted: "#3d5f4e",
  };
  const [result, setResult] = useState(null);
  const [snapshot, setSnapshot] = useState(null);

  useEffect(() => {
    if (open) {
      setResult(null);
      setSnapshot({
        currentLocation,
        currentFulfilment,
        willCancelLabel,
        willRestoreStock,
        labelOrder,
        target,
      });
    }
  }, [open]);

  const view = snapshot || {
    currentLocation,
    currentFulfilment,
    willCancelLabel,
    willRestoreStock,
    labelOrder,
    target,
  };
  const carrierCopy = getTransferCarrierCopy(view.labelOrder);
  const succeeded = result?.success === true;

  const handleConfirm = async () => {
    const response = await onConfirm();
    if (response) setResult(response);
  };

  const handleClose = () => {
    if (loading) return;
    setResult(null);
    onCancel();
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: THEME.light,
              border: `1px solid ${THEME.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <SwapOutlined style={{ color: THEME.primary, fontSize: 16 }} />
          </span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>
              Transfer order location
            </div>
            <div style={{ fontSize: 11, color: "#999", fontWeight: 400 }}>
              {view.willCancelLabel
                ? `Cancel the ${carrierCopy.carrierShort} label, then move the order`
                : "Move this order to the other fulfilment site"}
            </div>
          </div>
        </div>
      }
      open={open}
      onCancel={handleClose}
      footer={null}
      width={480}
      maskClosable={!loading}
      closable={!loading}
      destroyOnClose
    >
      <div style={{ padding: "4px 0 8px" }}>
        <p
          style={{
            fontSize: 13,
            color: "#666",
            lineHeight: 1.55,
            margin: "0 0 16px",
          }}
        >
          {view.willCancelLabel
            ? `The existing ${carrierCopy.carrierName} shipping label will be cancelled and the order will return to ${carrierCopy.pendingLabel} at the new location.${view.willRestoreStock ? " Deducted stock will be returned to inventory." : ""}`
            : "The order will stay in its current status so the new location can process and fulfil it."}
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            gap: 10,
            alignItems: "stretch",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              border: "1px solid #eee",
              borderRadius: 10,
              padding: "12px 14px",
              background: "#fafafa",
            }}
          >
            <p
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.7px",
                color: "#aaa",
                margin: "0 0 6px",
              }}
            >
              From
            </p>
            <div style={{ fontWeight: 600, fontSize: 13, color: "#333", lineHeight: 1.4 }}>
              {view.currentLocation}
            </div>
            {view.currentFulfilment && (
              <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                {view.currentFulfilment}
              </div>
            )}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              color: THEME.primary,
              fontSize: 16,
            }}
          >
            →
          </div>

          <div
            style={{
              border: `2px solid ${THEME.primary}`,
              borderRadius: 10,
              padding: "12px 14px",
              background: THEME.light,
            }}
          >
            <p
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.7px",
                color: THEME.primary,
                margin: "0 0 6px",
              }}
            >
              To
            </p>
            <div style={{ fontWeight: 600, fontSize: 13, color: THEME.primary, lineHeight: 1.4 }}>
              {view.target?.location}
            </div>
            {view.target?.fulfilmentBy && (
              <div style={{ fontSize: 12, color: THEME.muted, marginTop: 4 }}>
                {view.target.fulfilmentBy}
              </div>
            )}
          </div>
        </div>

        {result && (
          <div
            style={{
              borderRadius: 10,
              padding: "12px 14px",
              marginBottom: 16,
              background: succeeded ? "#f6ffed" : "#fff2f0",
              border: `1px solid ${succeeded ? "#b7eb8f" : "#ffccc7"}`,
              color: succeeded ? "#389e0d" : "#cf1322",
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            {result.message}
          </div>
        )}

        {succeeded ? (
          <Button
            type="primary"
            block
            size="large"
            onClick={handleClose}
            style={{
              background: THEME.primary,
              borderColor: THEME.primary,
              fontWeight: 600,
              fontSize: 14,
              height: 44,
            }}
          >
            Done
          </Button>
        ) : (
          <Button
            type="primary"
            block
            size="large"
            icon={<SwapOutlined />}
            loading={loading}
            disabled={!view.target || loading}
            onClick={handleConfirm}
            style={{
              background: view.target ? THEME.primary : undefined,
              borderColor: view.target ? THEME.primary : undefined,
              fontWeight: 600,
              fontSize: 14,
              height: 44,
            }}
          >
            Confirm transfer
          </Button>
        )}
      </div>
    </Modal>
  );
};

const RegenerateLabelModal = ({
  open,
  loading,
  orderId,
  carrierName,
  onCancel,
  onConfirm,
}) => {
  const THEME = {
    primary: "#4e7661",
    light: "#eef4f1",
    border: "#c5d9ce",
  };
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) setResult(null);
  }, [open]);

  const succeeded = result?.success === true;

  const handleConfirm = async () => {
    const response = await onConfirm();
    if (response) setResult(response);
  };

  const handleClose = () => {
    if (loading) return;
    setResult(null);
    onCancel();
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: THEME.light,
              border: `1px solid ${THEME.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PrinterOutlined style={{ color: THEME.primary, fontSize: 16 }} />
          </span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>
              Regenerate Shipping Label
            </div>
            <div style={{ fontSize: 11, color: "#999", fontWeight: 400 }}>
              {orderId ? `Order ${orderId}` : "Processed order"}
            </div>
          </div>
        </div>
      }
      open={open}
      onCancel={handleClose}
      footer={null}
      width={480}
      maskClosable={!loading}
      closable={!loading}
      destroyOnClose
    >
      <div style={{ padding: "4px 0 8px" }}>
        <p style={{ fontSize: 13, color: "#666", lineHeight: 1.55, margin: "0 0 16px" }}>
          {`This fetches the existing ${carrierName} shipping label for order ${orderId || "this order"}, merges it with a packing slip, and downloads the PDF. The order stays processed.`}
        </p>
        {result && (
          <div
            style={{
              borderRadius: 10,
              padding: "12px 14px",
              marginBottom: 16,
              background: succeeded ? "#f6ffed" : "#fff2f0",
              border: `1px solid ${succeeded ? "#b7eb8f" : "#ffccc7"}`,
              color: succeeded ? "#389e0d" : "#cf1322",
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            {result.message}
          </div>
        )}
        {succeeded ? (
          <Button
            type="primary"
            block
            size="large"
            onClick={handleClose}
            style={{ background: THEME.primary, borderColor: THEME.primary, fontWeight: 600, height: 44 }}
          >
            Done
          </Button>
        ) : (
          <Button
            type="primary"
            block
            size="large"
            icon={<PrinterOutlined />}
            loading={loading}
            onClick={handleConfirm}
            style={{ background: THEME.primary, borderColor: THEME.primary, fontWeight: 600, height: 44 }}
          >
            Confirm and download
          </Button>
        )}
      </div>
    </Modal>
  );
};

const SpecialDeliveryModal = ({
  open,
  loading,
  currentPostageCode,
  onCancel,
  onConfirm,
}) => {
  const THEME = {
    primary: "#4e7661",
    light: "#eef4f1",
    border: "#c5d9ce",
  };
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) setResult(null);
  }, [open]);

  const succeeded = result?.success === true;

  const handleConfirm = async () => {
    const response = await onConfirm();
    if (response) setResult(response);
  };

  const handleClose = () => {
    if (loading) return;
    setResult(null);
    onCancel();
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: THEME.light,
              border: `1px solid ${THEME.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ThunderboltOutlined style={{ color: THEME.primary, fontSize: 16 }} />
          </span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>
              Set Special Delivery
            </div>
            <div style={{ fontSize: 11, color: "#999", fontWeight: 400 }}>
              Royal Mail Click & Drop service SEB
            </div>
          </div>
        </div>
      }
      open={open}
      onCancel={handleClose}
      footer={null}
      width={480}
      maskClosable={!loading}
      closable={!loading}
      destroyOnClose
    >
      <div style={{ padding: "4px 0 8px" }}>
        <p style={{ fontSize: 13, color: "#666", lineHeight: 1.55, margin: "0 0 16px" }}>
          {`The postage code will change from ${currentPostageCode || "none"} to SEB. The order will be sent as Royal Mail Special Delivery the next time it is submitted to Click & Drop.`}
        </p>
        {result && (
          <div
            style={{
              borderRadius: 10,
              padding: "12px 14px",
              marginBottom: 16,
              background: succeeded ? "#f6ffed" : "#fff2f0",
              border: `1px solid ${succeeded ? "#b7eb8f" : "#ffccc7"}`,
              color: succeeded ? "#389e0d" : "#cf1322",
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            {result.message}
          </div>
        )}
        {succeeded ? (
          <Button
            type="primary"
            block
            size="large"
            onClick={handleClose}
            style={{ background: THEME.primary, borderColor: THEME.primary, fontWeight: 600, height: 44 }}
          >
            Done
          </Button>
        ) : (
          <Button
            type="primary"
            block
            size="large"
            icon={<ThunderboltOutlined />}
            loading={loading}
            onClick={handleConfirm}
            style={{ background: THEME.primary, borderColor: THEME.primary, fontWeight: 600, height: 44 }}
          >
            Confirm Special Delivery
          </Button>
        )}
      </div>
    </Modal>
  );
};

const getResetOrderPoints = ({ carrierCopy, isShipped, willRestoreStock }) => {
  const isAmazon = carrierCopy.carrierName === "Amazon Shipping";
  const points = [];

  if (isShipped) {
    points.push(
      {
        title: "Tracking is checked first",
        detail: "The label is cancelled only after tracking details are loaded.",
      },
      {
        title: isAmazon ? "No events, or only ReadyForReceive" : "No tracking events",
        detail: `The ${carrierCopy.carrierName} label is cancelled and the order returns to ${carrierCopy.pendingLabel}.`,
      },
    );
  } else {
    points.push({
      title: "Label will be cancelled",
      detail: `The existing ${carrierCopy.carrierName} label is cancelled and the order returns to ${carrierCopy.pendingLabel}.`,
    });
  }

  points.push({
    title: "Location stays the same",
    detail: "The order is not moved to another site.",
  });

  if (willRestoreStock) {
    points.push({
      title: "Stock is returned",
      detail: "Deducted stock goes back to inventory.",
    });
  }

  points.push({
    title: "Order log is kept",
    detail: "Previous courier activity stays on the order.",
  });

  if (isShipped) {
    points.push({
      title: "Manifested shipments stay shipped",
      detail: isAmazon
        ? "Any event after ReadyForReceive blocks the reset."
        : "Any tracking event blocks the reset.",
      tone: "stop",
    });
  }

  return points;
};

const ResetOrderPoints = ({ points, accent, accentSoft, accentBorder }) => (
  <div
    style={{
      border: `1px solid ${accentBorder}`,
      borderRadius: 10,
      overflow: "hidden",
      marginBottom: 16,
    }}
  >
    {points.map((point, index) => {
      const stopped = point.tone === "stop";
      return (
        <div
          key={point.title}
          style={{
            display: "flex",
            gap: 10,
            alignItems: "flex-start",
            padding: "10px 12px",
            background: stopped ? "#fff6f5" : index % 2 === 0 ? "#fff" : accentSoft,
            borderTop: index ? `1px solid ${stopped ? "#ffd6d1" : accentBorder}` : "none",
          }}
        >
          <span
            style={{
              width: 18,
              height: 18,
              borderRadius: 6,
              flexShrink: 0,
              marginTop: 1,
              background: stopped ? "#cf1322" : accent,
              color: "#fff",
              fontSize: 11,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {index + 1}
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: stopped ? "#a8071a" : "#1f2a26", lineHeight: 1.35 }}>
              {point.title}
            </div>
            <div style={{ fontSize: 12, fontWeight: 500, color: "#5c6b64", lineHeight: 1.45, marginTop: 2 }}>
              {point.detail}
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

const ResetOrderModal = ({
  open,
  loading,
  carrierCopy,
  isShipped,
  willRestoreStock,
  onCancel,
  onConfirm,
}) => {
  const THEME = {
    primary: "#4e7661",
    light: "#eef4f1",
    border: "#c5d9ce",
  };
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) setResult(null);
  }, [open]);

  const succeeded = result?.success === true;

  const handleConfirm = async () => {
    const response = await onConfirm();
    if (response) setResult(response);
  };

  const handleClose = () => {
    if (loading) return;
    setResult(null);
    onCancel();
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: THEME.light,
              border: `1px solid ${THEME.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <UndoOutlined style={{ color: THEME.primary, fontSize: 16 }} />
          </span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>
              Reset order to Ready
            </div>
            <div style={{ fontSize: 11, color: "#999", fontWeight: 400 }}>
              {`Cancel the ${carrierCopy.carrierShort} label so it can be sent again`}
            </div>
          </div>
        </div>
      }
      open={open}
      onCancel={handleClose}
      footer={null}
      width={480}
      maskClosable={false}
      closable={false}
      destroyOnClose
    >
      <div style={{ padding: "4px 0 8px" }}>
        <ResetOrderPoints
          points={getResetOrderPoints({ carrierCopy, isShipped, willRestoreStock })}
          accent={THEME.primary}
          accentSoft={THEME.light}
          accentBorder={THEME.border}
        />
        {result && (
          <div
            style={{
              borderRadius: 10,
              padding: "12px 14px",
              marginBottom: 16,
              background: succeeded ? "#f6ffed" : "#fff2f0",
              border: `1px solid ${succeeded ? "#b7eb8f" : "#ffccc7"}`,
              color: succeeded ? "#389e0d" : "#cf1322",
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            {result.message}
          </div>
        )}
        {succeeded ? (
          <Button
            type="primary"
            block
            size="large"
            onClick={handleClose}
            style={{ background: THEME.primary, borderColor: THEME.primary, fontWeight: 600, height: 44 }}
          >
            Done
          </Button>
        ) : (
          <div style={{ display: "flex", gap: 10 }}>
            <Button
              block
              size="large"
              disabled={loading}
              onClick={handleClose}
              style={{ height: 44, fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              block
              size="large"
              icon={<UndoOutlined />}
              loading={loading}
              onClick={handleConfirm}
              style={{ background: THEME.primary, borderColor: THEME.primary, fontWeight: 600, height: 44 }}
            >
              Confirm reset
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};

const RepeatOffenderModal = ({ visible, onCancel, onConfirm, loading }) => {
  const [reason, setReason] = useState('');

  const handleSubmit = async () => {
    if (!reason.trim()) {
      message.error('Please provide a reason');
      return;
    }

    try {
      await onConfirm(reason);
      setReason('');
      onCancel();
    } catch (error) {
      message.error('Failed to mark as repeat offender');
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="back" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
        >
          Confirm
        </Button>,
      ]}
    >
      <div style={{ padding: '20px' }}>
        <h5 className='text-center p-0 m-0'>Are you sure you want to mark this customer</h5>
        <h4 className='text-center p-0 m-0'>as</h4>
        <h4 className='text-center text-danger p-0 mb-4'>Repeat offender?</h4>
        <TextArea
          rows={4}
          placeholder="Enter the reason for offender..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required
        />
      </div>
    </Modal>
  );
};


// Prop Types
OrderDetailCard.propTypes = {
  order: PropTypes.object.isRequired,
  setOrder: PropTypes.func.isRequired,
  openCases: PropTypes.array,
  openEscalations: PropTypes.array,
  setOpenEscalations: PropTypes.func,
  customer: PropTypes.object,
  assessment: PropTypes.object,
  idCheckSummary: PropTypes.object,
  setIdCheckSummary: PropTypes.func,
  setCustomer: PropTypes.func,
  canMarkVerified: PropTypes.bool,
  mailLogs: PropTypes.array,
  canEdit: PropTypes.bool,
  canStatusEdit: PropTypes.bool,
  canAutomation: PropTypes.bool,
  reminderLoading: PropTypes.bool,
  reminder: PropTypes.number,
  handleReminderChange: PropTypes.func,
  orderStatusChange: PropTypes.func,
  onLocationTransferred: PropTypes.func
};

export default OrderDetailCard;